import { useState } from 'react';
import { useCan, useSubjectLanguageVariants } from '@features/user';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { Badge, Button } from '@mui/material';
import { FeatureGates, useEventTracking, useFeatureGate } from '@services/statsig';

import { useBookingRequestsQuery } from '../store/api';

import { BookingRequestsPanel } from './BookingRequestsPanel';

/** Calendar-header entry point to the pending booking-requests queue.
 *
 * Renders nothing unless the self-scheduling feature gate is on AND the user
 * can manage the org calendar (the queue API is manager-gated). The badge
 * shows the pending count.
 */
export function BookingRequestsButton() {
  const schedulingFeatureEnabled = useFeatureGate(
    FeatureGates.PATIENT_SELF_SCHEDULING,
  );
  const canManageScheduling = useCan('manage_org_calendar');
  const [open, setOpen] = useState(false);
  const { bookingRequests } = useEventTracking();
  const { plural } = useSubjectLanguageVariants();

  const enabled = schedulingFeatureEnabled && canManageScheduling;
  const { data, isLoading } = useBookingRequestsQuery(
    {},
    { skip: !enabled, pollingInterval: 60_000 },
  );

  if (!enabled) {
    return null;
  }

  const items = (data?.items ?? []).map(item => ({
    bookingProspectId: item.booking_prospect_id,
    appointmentId: item.appointment_id,
    appointmentStatus: item.appointment_status,
    appointmentStartTime: item.appointment_start_time,
    firstName: item.first_name,
    lastName: item.last_name,
    email: item.email,
    phone: item.phone,
    reason: item.reason,
  }));
  // Confirmed bookings stay listed (identity review is a later step) but are
  // no longer waiting on the user — only unconfirmed ones light the badge.
  const pendingCount = items.filter(
    item => item.appointmentStatus !== 'scheduled',
  ).length;

  const handleOpen = () => {
    bookingRequests.opened({ pending_count: pendingCount });
    setOpen(true);
  };

  return (
    <>
      <Badge badgeContent={pendingCount} color="warning" overlap="rectangular">
        <Button
          variant="outlined"
          size="small"
          startIcon={<InboxOutlinedIcon />}
          onClick={handleOpen}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          Requests
        </Button>
      </Badge>
      <BookingRequestsPanel
        open={open}
        onClose={() => setOpen(false)}
        items={items}
        isLoading={isLoading}
        subjectPlural={plural}
      />
    </>
  );
}
